<template>
  <div class="vis-container">
    <VisVarsDialog ref="varsDialog" />

    <!-- Esperando dados -->
    <div v-if="isLoading" class="center-wrapper">
      <OJumbo class="mt-8" spin />
    </div>

    <!-- Dados não chegaram ou chegaram com erro -->
    <div v-else-if="err != null" class="center-wrapper">
      <VisError :err="err" :orgCode="orgCode" :tripId="tripId" />
    </div>

    <!-- Chegaram normalmente -->
    <template v-else>
      <div class="v-row-stretch overflow-hidden">
        <!-- top bar com id, abas, e botões -->
        <div class="h-row grey lighten-2">
          <div class="subheading px-4 flex-shrink-0 font-weight-bold">
            Trecho #{{ tripId }}
          </div>

          <v-spacer />

          <div>
            <v-tabs right v-model="currentTab" background-color="transparent">
              <v-tab href="#1">Geral</v-tab>
              <v-tab href="#2">JSON</v-tab>
              <v-tab href="#3">Rawdata</v-tab>
              <v-tab href="#4">Parsed Data</v-tab>
            </v-tabs>
          </div>

          <template v-if="openInNewVisible">
            <v-spacer />

            <v-btn icon target="_blank" :to="openInNewUrl">
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn>
            <v-btn icon class="mx-2" @click="$emit('click:dismiss')">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </div>

        <v-tabs-items
          class="fill-height fill-width overflow-hidden"
          touchless
          v-model="currentTab"
        >
          <v-tab-item value="1" class="fill-height">
            <div class="h-row-stretch">
              <div class="vis-sidebar">
                <VisSidebar
                  :tripData="data"
                  :width="sideW"
                  @click:vars="changeVars"
                />
              </div>

              <div class="vis-main">
                <VisMain
                  :tripData="data"
                  :sel="sel"
                  @chartResize="saveChartSize"
                />
              </div>
            </div>
          </v-tab-item>

          <v-tab-item value="2" class="fill-height overflow-hidden">
            <VisJson :value="data" @forceClosed="onForceClosed" />
          </v-tab-item>

          <v-tab-item value="3" class="fill-height overflow-hidden">
            <VisRawdata :value="data" />
          </v-tab-item>

          <v-tab-item value="4" class="fill-height overflow-hidden">
            <VisParsedData :value="data" />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </template>
  </div>
</template>

<script>
import { OrgApi } from '@/models'
import { cloneWithOverrides } from 'trip-vars'
import FetchVisData from './FetchVisData'
import { savePref, getPref } from 'oderon-vue/Prefs'
import { isEmpty } from 'oderon-commons/src/utils'
import moment from 'moment'

import VisSidebar from './VisSidebar.vue'
import VisMain from './VisMain.vue'
import VisVarsDialog from './VisVarsDialog.vue'
import VisError from './VisError.vue'
import VisJson from './VisJson.vue'
import VisRawdata from './VisRawdata.vue'
import VisParsedData from './VisParsedData.vue'

export default {
  components: {
    VisSidebar,
    VisMain,
    VisError,
    VisVarsDialog,
    VisJson,
    VisRawdata,
    VisParsedData
  },
  props: {
    orgCode: { required: true },
    tripId: { type: Number, required: true },
    // lapseIndex: { type: Number }, // deprecated
    // range: { type: String },
    // timestamp: { type: String },
    openInNewVisible: { type: Boolean, default: false },
    // showExportDialog: { type: Boolean, default: false },
    prefsKeyPrefix: { type: String, required: true }
  },
  data() {
    return {
      isLoading: true,
      currentTab: '1',
      currentVars: getPref(`oderonVis${this.prefsKeyPrefix}Vars`, [
        'rpm',
        'speed',
        'brake',
        'brakeWithSpeed',
        'gps',
        'dg1'
      ]),

      data: null,
      err: null,

      varRegistry: null
    }
  },
  computed: {
    openInNewUrl() {
      if (!this.openInNewVisible) return null
      return `/orgs/${this.data.org.name}/trip/${this.data.mainTrip.id}`
    },
    maxW() {
      return document.documentElement.clientWidth
    },
    maxH() {
      return document.documentElement.clientHeight
    },
    isSm() {
      return this.$vuetify.breakpoint.smAndDown
    },
    sideW() {
      return this.isSm ? 0 : 300
    },
    h() {
      let topBarH = this.isSm ? 56 : 64
      return this.maxH - topBarH
      // if ( this.isSm ) return this.maxH
      // else return this.maxH - 120
    },
    w() {
      return this.maxW
      // if ( this.isSm ) return this.maxW
      // else return this.maxW - 120
    },

    sel() {
      if (!isEmpty(this.lapseIndex) && this.trip != null) {
        return moment(this.trip.startTimestamp).add(this.lapseIndex, 's')
      }

      if (!isEmpty(this.timestamp)) return this.timestamp

      if (!isEmpty(this.range)) return this.range

      return null
    }
  },
  mounted() {
    this.reload()
  },
  watch: {
    tripId() {
      this.reload()
    }
  },
  methods: {
    reload() {
      this.data = null
      this.$loading(() => this.fetchData())
    },
    async fetchData() {
      try {
        // await this.$delay(2000)

        // let err = new Error()
        // err.reason = 'MissingRawdata'
        // err.status = 403
        // err.reqId = 1234
        // throw err

        // Só precisamos do veículo, para montar o varRegistry
        // que é usado pelo VisVarsDialog. Observe que este
        // varRegistry NÃO é o que será usado depois para montar
        // a visualização!
        let fetcher = new OrgApi(this.orgCode)
        let trip = await fetcher.get(`trips/${this.tripId}`)
        this.varRegistry = cloneWithOverrides(trip.vehicle.configs)

        // Busca os dados a serem exibidos, incluindo novamente
        // infos do veículo, driver, etc
        this.data = await FetchVisData({
          orgCode: this.orgCode,
          tripId: this.tripId,
          vars: this.currentVars,
          isMobile: this.isSm,
          chartHeightPx: getPref(`oderonVis${this.prefsKeyPrefix}CHPX`)
        })
      } catch (err) {
        this.err = err
      }
    },

    async changeVars() {
      let curVars = this.currentVars,
        dialog = this.$refs.varsDialog,
        newVars = await dialog.open(curVars, this.varRegistry)

      if (newVars != null) {
        this.currentVars = newVars
        savePref(`oderonVis${this.prefsKeyPrefix}Vars`, newVars)
        this.$loading(() => this.fetchData())
      }
    },

    saveChartSize(evt) {
      let { chartHeightPx } = evt
      savePref(`oderonVis${this.prefsKeyPrefix}CHPX`, chartHeightPx)
    },

    onForceClosed() {
      this.$emit('forceClosed')
      this.reload()
    }
  }
}
</script>

<style lang="sass">
.center-wrapper
  width: 100%
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: center

.vis-container
  background-color: white
  position: relative
  display: flex
  flex-flow: row nowrap
  align-items: stretch
  justify-content: stretch
  // height: calc(100vh - 64px)
  height: 100%
  width: 100%

  .v-row-stretch
    display: flex
    flex-flow: column nowrap
    width: 100%
    height: 100%
    justify-content: stretch

  .h-row-stretch
    display: flex
    flex-flow: row nowrap
    width: 100%
    height: 100%
    justify-content: stretch

.vis-sidebar
  // height: calc(100vh - 64px)
  height: 100%
  overflow: hidden
  z-index: 2
  box-shadow: 1px 0 3px #999
  background-color: white

.vis-main
  flex-grow: 1
  z-index: 1
</style>
